import { useQuery } from "@tanstack/react-query";

import { Badge, Card, Spinner } from "@/components/ui/card";
import { api, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const TONE = { green: "success", yellow: "warning", red: "danger", unknown: "neutral" } as const;
const BAR = {
  green: "bg-success",
  yellow: "bg-warning",
  red: "bg-danger",
  unknown: "bg-muted-foreground",
} as const;
const LEVEL = {
  green: "ostore.level_green",
  yellow: "ostore.level_yellow",
  red: "ostore.level_red",
  unknown: "ostore.level_unknown",
} as const;

/** Rasm/video ombori (B2/R2): bepul hajmning qancha ishlatilgani va undan keyingi taxminiy narx. */
export function StorageCard() {
  const { t } = useT();
  const q = useQuery({
    queryKey: ["object-storage"],
    queryFn: async () => unwrap(await api.GET("/api/hosting/object-storage/")),
  });
  const s = q.data;
  if (q.isLoading) return <Spinner className="mx-auto my-6 size-6" />;
  if (!s) return null;
  if (!s.configured) {
    return (
      <Card data-testid="storage-card">
        <h2 className="mb-1 font-bold">{t("ostore.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("ostore.not_configured")}</p>
      </Card>
    );
  }
  const pct = Math.min(s.percent, 100);
  return (
    <Card data-testid="storage-card">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-bold">{t("ostore.title")}</h2>
        <Badge tone={TONE[s.level]}>{t(LEVEL[s.level])}</Badge>
      </div>
      <div className="mb-1 text-2xl font-bold">
        {s.used_gb}{" "}
        <span className="text-base font-normal text-muted-foreground">/ {s.free_gb} GB</span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="mb-2 h-3 overflow-hidden rounded-full bg-muted"
      >
        <div className={cn("h-full rounded-full", BAR[s.level])} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-sm text-muted-foreground">
        {s.billable_gb > 0
          ? t("ostore.paid_note", { gb: s.billable_gb, usd: s.estimated_monthly_usd })
          : t("ostore.free_note", { bucket: s.bucket })}
      </p>
    </Card>
  );
}
